import { ArrowDown, ArrowUp, Trash2 } from "lucide-react";

import { Badge } from "@/common/ui/badge";
import { Button } from "@/common/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/common/ui/table";

import type { WaitlistSignup } from "./types";
import type { SortDirection } from "./use-waitlist";

interface WaitlistTableProps {
  signups: WaitlistSignup[];
  sortDirection: SortDirection;
  onToggleSort: () => void;
  onDelete: (signup: WaitlistSignup) => void;
}

const dateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatSource(source: string): string {
  return source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function WaitlistTable({
  signups,
  sortDirection,
  onToggleSort,
  onDelete,
}: WaitlistTableProps): React.ReactElement {
  return (
    <div data-testid="waitlist-table">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead className="hidden xs:table-cell">
              <Button
                data-testid="sort-date-button"
                variant="ghost"
                size="sm"
                className="-ml-3 h-8"
                onClick={onToggleSort}
              >
                Date
                {sortDirection === "desc" ? (
                  <ArrowDown className="ml-1 h-4 w-4" />
                ) : (
                  <ArrowUp className="ml-1 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="hidden sm:table-cell">Source</TableHead>
            <TableHead className="w-[50px]">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {signups.map((signup) => (
            <TableRow key={signup.id} data-testid={`signup-row-${signup.id}`}>
              <TableCell className="font-medium">{signup.email}</TableCell>
              <TableCell className="hidden xs:table-cell">
                {dateFormatter.format(new Date(signup.createdAt))}
              </TableCell>
              <TableCell className="hidden sm:table-cell">
                <Badge variant="secondary">{formatSource(signup.source)}</Badge>
              </TableCell>
              <TableCell>
                <Button
                  data-testid={`delete-button-${signup.id}`}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => onDelete(signup)}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete {signup.email}</span>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
